const DB_NAME = "connectyid-offline";
const DB_VERSION = 1;
export const QUEUE_STORE = "write-queue";

let dbPromise: Promise<IDBDatabase> | null = null;

const openDb = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        // autoIncrement gives FIFO ordering for free: key order === insertion order.
        db.createObjectStore(QUEUE_STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
};

const run = <T>(
  store: string,
  mode: IDBTransactionMode,
  work: (objectStore: IDBObjectStore) => IDBRequest
): Promise<T> =>
  openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const request = work(tx.objectStore(store));
        request.onsuccess = () => resolve(request.result as T);
        request.onerror = () => reject(request.error);
      })
  );

export const dbGetAll = <T>(store: string): Promise<T[]> => run<T[]>(store, "readonly", (s) => s.getAll());

export const dbGet = <T>(store: string, id: number): Promise<T | undefined> =>
  run<T | undefined>(store, "readonly", (s) => s.get(id));

export const dbAdd = <T>(store: string, value: T): Promise<number> =>
  run<IDBValidKey>(store, "readwrite", (s) => s.add(value)).then((key) => Number(key));

export const dbPut = <T>(store: string, value: T): Promise<void> =>
  run<IDBValidKey>(store, "readwrite", (s) => s.put(value)).then(() => undefined);

export const dbDelete = (store: string, id: number): Promise<void> =>
  run<undefined>(store, "readwrite", (s) => s.delete(id)).then(() => undefined);

export const dbClear = (store: string): Promise<void> =>
  run<undefined>(store, "readwrite", (s) => s.clear()).then(() => undefined);

/** Test-only: drop the memoised connection so a new one is opened. */
export const __resetDbForTests = (): void => {
  dbPromise = null;
};
